// Review-only session — plays a subject's most-overdue misses through the lean
// QuizCanvas player and persists each answer via pastExamAPI (the SAME path as
// normal practice), so a correct answer auto-graduates the item out of review.
//
// The 🔁 chip + "Recovered ✅" moment ride the QuizCanvas `contextLabel` prop, so
// the shared player needs no changes.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { MediaItem } from '../components/MicroLearning/MediaDisplay';
import QuizCanvas, { ACCENTS } from '../components/ExamRun/QuizCanvas';
import { displayTopic } from '../config/topicDisplayNames';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { pastExamAPI, track } from '../services/api';
import { MissesQuestion, reviewAPI } from '../services/reviewAPI';

interface LastAnswer {
  is_correct: boolean;
  correct_answer: string;
  explanation?: string;
}

// Recap design tokens — mirror the Daily Focus / Aptitude recaps so the misses
// completion screen reads as part of the same family (orange = the misses accent).
const mmTokens = (dark: boolean) => ({
  primary: '#ea580c',
  bg: dark ? '#0f1216' : '#f8fafc',
  border: dark ? '#262c35' : '#e2e8f0',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  card: dark ? '#171b21' : 'white',
  backArrow: dark ? '#aab1ba' : '#475569',
});
const mmCard = (dark: boolean): React.CSSProperties => ({
  background: dark ? '#171b21' : 'white',
  border: `1px solid ${mmTokens(dark).border}`,
  borderRadius: 14,
  padding: 16,
  boxShadow: dark ? '0 1px 0 rgba(0,0,0,0.4)' : '0 1px 0 rgba(0,0,0,0.02)',
});
const mmPrimaryBtn = (dark: boolean): React.CSSProperties => ({
  marginTop: 16,
  width: '100%',
  padding: '12px 16px',
  background: mmTokens(dark).primary,
  color: 'white',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
});
const mmSecondaryBtn = (dark: boolean): React.CSSProperties => ({
  marginTop: 10,
  width: '100%',
  padding: '10px 14px',
  background: 'transparent',
  color: mmTokens(dark).textPrimary,
  border: `1px solid ${mmTokens(dark).border}`,
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
});

// Sticky-header shell so every terminal state (loading / caught-up / recap) sits
// in the same frame as the rest of the practice flows.
const MissesShell: React.FC<{ onBack: () => void; children: React.ReactNode }> = ({
  onBack,
  children,
}) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const MM = mmTokens(dark);
  return (
  <div style={{ minHeight: '100vh', background: MM.bg }}>
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        background: MM.card,
        borderBottom: `1px solid ${MM.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          background: 'transparent',
          border: 'none',
          padding: 6,
          cursor: 'pointer',
          color: MM.backArrow,
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ←
      </button>
      <h1 style={{ margin: 0, fontSize: '1rem', color: MM.textPrimary }}>
        Master your misses
      </h1>
    </header>
    <main style={{ maxWidth: 520, margin: '0 auto', padding: '16px 16px 80px' }}>
      {children}
    </main>
  </div>
  );
};

const MasterYourMissesSession: React.FC = () => {
  const { subject = '' } = useParams<{ subject: string }>();
  const navigate = useNavigate();
  const { student } = useAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const MM = mmTokens(dark);
  const studentId = student?.student_id || '';

  const [questions, setQuestions] = useState<MissesQuestion[] | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [lastAnswer, setLastAnswer] = useState<LastAnswer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [qStartedAt, setQStartedAt] = useState<number>(() => Date.now());
  const [answeredCount, setAnsweredCount] = useState(0);
  const [recovered, setRecovered] = useState(0);
  // Per-question outcomes, so the recap can show a by-topic breakdown like the
  // Daily Focus / Aptitude recaps (not just a single number).
  const [results, setResults] = useState<Array<{ topic: string; isCorrect: boolean }>>([]);
  // Bumped by "Review more" to re-fetch the next batch in the same subject.
  const [reloadKey, setReloadKey] = useState(0);

  const reviewMore = useCallback(() => {
    setQuestions(null);
    setSessionId(null);
    setIdx(0);
    setSelected(null);
    setLastAnswer(null);
    setAnsweredCount(0);
    setRecovered(0);
    setResults([]);
    setQStartedAt(Date.now());
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    reviewAPI
      .session(subject)
      .then(async (data) => {
        if (cancelled) return;
        const qs = data.questions || [];
        setQuestions(qs);
        track('review.session.started', { subject, count: qs.length });
        if (qs.length && studentId) {
          try {
            const s = await pastExamAPI.startSession({
              student_id: studentId,
              subject,
              question_ids: qs.map((q) => q.question_id),
            });
            if (!cancelled) setSessionId(s?.session_id ?? null);
          } catch {
            // Session start failed — questions still playable, answers just
            // won't persist this run.
          }
        }
      })
      .catch(() => {
        if (!cancelled) setQuestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [subject, studentId, reloadKey]);

  const currentQ =
    questions && idx < questions.length ? questions[idx] : null;

  const handleSelect = useCallback(
    (label: string) => {
      if (!currentQ || submitting || lastAnswer) return;
      const correct = currentQ.metadata?.correct_answer || '';
      const isCorrect = label === correct;
      setSelected(label);
      setSubmitting(true);
      setLastAnswer({
        is_correct: isCorrect,
        correct_answer: correct,
        explanation: currentQ.metadata?.explanation,
      });
      setAnsweredCount((c) => c + 1);
      if (isCorrect) setRecovered((r) => r + 1);
      setResults((rs) => [
        ...rs,
        { topic: currentQ.metadata?.topic || 'Other', isCorrect },
      ]);
      const timeSpent = Math.max(1, Math.round((Date.now() - qStartedAt) / 1000));
      if (sessionId && studentId) {
        pastExamAPI
          .saveAnswer(sessionId, {
            student_id: studentId,
            question_id: currentQ.question_id,
            selected_answer: label,
            is_correct: isCorrect,
            time_spent_seconds: timeSpent,
            topic: currentQ.metadata?.topic,
            is_review: true,
          })
          .catch(() => {});
      }
      setSubmitting(false);
    },
    [currentQ, submitting, lastAnswer, sessionId, studentId, qStartedAt]
  );

  const handleNext = useCallback(() => {
    setSelected(null);
    setLastAnswer(null);
    setQStartedAt(Date.now());
    setIdx((i) => i + 1);
  }, []);

  if (questions === null) {
    return (
      <MissesShell onBack={() => navigate('/dashboard')}>
        <div style={{ ...mmCard(dark), textAlign: 'center', color: MM.textMuted }}>
          Loading your misses…
        </div>
      </MissesShell>
    );
  }

  if (!currentQ) {
    const reviewed = answeredCount;

    // Caught up — nothing was due (or nothing left). A complete, designed state,
    // never a bare line.
    if (reviewed === 0) {
      return (
        <MissesShell onBack={() => navigate('/dashboard')}>
          <div style={mmCard(dark)}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>🎯</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: MM.textPrimary, marginTop: 10 }}>
              All caught up
            </div>
            <div style={{ marginTop: 6, color: MM.textMuted, fontSize: 14 }}>
              No {subject} misses to review right now — come back after your next
              practice and Lomi will resurface what to lock in.
            </div>
            <button onClick={() => navigate('/dashboard')} style={mmPrimaryBtn(dark)}>
              Back to dashboard
            </button>
          </div>
        </MissesShell>
      );
    }

    // Recap, modeled on the Daily Focus / Aptitude recaps.
    const byTopic: Record<string, { correct: number; answered: number }> = {};
    results.forEach((r) => {
      const t = displayTopic(r.topic) || r.topic;
      if (!byTopic[t]) byTopic[t] = { correct: 0, answered: 0 };
      byTopic[t].answered += 1;
      if (r.isCorrect) byTopic[t].correct += 1;
    });
    const topics = Object.entries(byTopic);
    const pct = reviewed > 0 ? Math.round((recovered / reviewed) * 100) : 0;
    const weakest =
      topics.length > 1
        ? topics
            .map(([topic, b]) => ({ topic, ...b }))
            .sort((a, b) => a.correct / a.answered - b.correct / b.answered)[0]
        : null;
    const subhead =
      recovered === reviewed
        ? 'Perfect recovery — these are yours now. 🎉'
        : recovered === 0
        ? 'Tough set — Lomi will bring these back tomorrow so they stick.'
        : `${pct}% recovered — turning mistakes into mastery.`;

    return (
      <MissesShell onBack={() => navigate('/dashboard')}>
        <div style={mmCard(dark)}>
          <div style={{ fontSize: 22, fontWeight: 700, color: MM.textPrimary }}>
            🔥 {recovered} of {reviewed} recovered
          </div>
          <div style={{ marginTop: 4, color: MM.textMuted, fontSize: 14 }}>{subhead}</div>

          {weakest && weakest.correct < weakest.answered && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: dark ? 'rgba(245,176,65,0.12)' : '#fef9c3',
                border: `1px solid ${dark ? 'rgba(245,176,65,0.35)' : '#fde047'}`,
                borderRadius: 10,
                color: dark ? '#f5c563' : '#713f12',
                fontSize: 14,
              }}
            >
              <strong>Still shaky:</strong> {weakest.topic} — {weakest.correct}/
              {weakest.answered}
            </div>
          )}

          {topics.length > 1 && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: MM.textMuted,
                  marginBottom: 6,
                }}
              >
                By topic
              </div>
              {topics.map(([topic, b]) => (
                <div
                  key={topic}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                    borderBottom: `1px solid ${MM.border}`,
                    fontSize: 14,
                  }}
                >
                  <span>{topic}</span>
                  <span style={{ color: MM.textMuted }}>
                    {b.correct}/{b.answered}
                  </span>
                </div>
              ))}
            </div>
          )}

          <button onClick={reviewMore} style={mmPrimaryBtn(dark)}>
            🔁 Review more {subject} misses
          </button>
          <button onClick={() => navigate('/dashboard')} style={mmSecondaryBtn(dark)}>
            Back to dashboard
          </button>
          <div
            style={{
              marginTop: 12,
              color: MM.textMuted,
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            Recovered misses get re-checked tomorrow to lock them in.
          </div>
        </div>
      </MissesShell>
    );
  }

  const reviewNote = lastAnswer
    ? lastAnswer.is_correct
      ? '✅ Recovered! You got this wrong before — now it’s yours.'
      : '🔁 Not yet — Lomi will bring it back.'
    : '🔁 You missed this before — another shot';
  const topicDisp = currentQ.metadata?.topic
    ? displayTopic(currentQ.metadata.topic) || currentQ.metadata.topic
    : '';
  const contextLabel = [reviewNote, topicDisp].filter(Boolean).join(' · ');

  return (
    <QuizCanvas
      accent={ACCENTS.daily}
      onBack={() => navigate('/dashboard')}
      answered={answeredCount}
      qNumber={idx + 1}
      total={questions.length}
      contextLabel={contextLabel}
      questionText={currentQ.question_text}
      media={(currentQ.media || []) as MediaItem[]}
      options={currentQ.options.map((o) => ({ label: o.label, text: o.text }))}
      selected={selected}
      lastAnswer={lastAnswer}
      submitting={submitting}
      onSelect={handleSelect}
      onNext={handleNext}
      isLast={idx === questions.length - 1}
      questionId={currentQ.question_id}
      translateSurface="review_misses_explanation"
    />
  );
};

export default MasterYourMissesSession;
